
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { MedicalAlert } from "@/types";

type StudentFormProps = {
  student?: any;
  onClose: () => void;
};

export function StudentForm({ student, onClose }: StudentFormProps) {
  const [formData, setFormData] = useState({
    surname: student?.surname || "",
    other_names: student?.other_names || "",
    class: student?.class || "",
    stream: student?.stream || "",
    tutor1_name: student?.tutor1_name || "",
    tutor1_contact: student?.tutor1_contact || "",
    tutor2_name: student?.tutor2_name || "",
    tutor2_contact: student?.tutor2_contact || "",
    emergency_contact_name: student?.emergency_contact_name || "",
    emergency_contact_phone: student?.emergency_contact_phone || "",
    sex: student?.sex || "",
    section: student?.section || "DAY",
    dormitory: student?.dormitory || "",
    dormitory_mother: student?.dormitory_mother || "",
    dormitory_mother_contact: student?.dormitory_mother_contact || "",
    age: student?.age || "",
    medical_alerts: student?.medical_alerts || [],
  });

  const { user } = useAuth();
  const { toast } = useToast();

  const { data: schoolData, isLoading: schoolLoading } = useQuery({
    queryKey: ['school', user?.schoolId],
    queryFn: async () => {
      if (!user?.schoolId) {
        console.log('No school ID found');
        return null;
      }
      console.log('Fetching school data for ID:', user.schoolId);
      const { data, error } = await supabase
        .from('schools')
        .select('prefix')
        .eq('id', user.schoolId)
        .single();
      
      if (error) {
        console.error('Error fetching school data:', error);
        throw error;
      }
      console.log('School data received:', data);
      return data;
    },
  });

  const isTISP = schoolData?.prefix === "TISP";
  console.log('School prefix:', schoolData?.prefix);
  console.log('Is TISP school:', isTISP);
  console.log('Current section:', formData.section);

  const { data: classes = [] } = useQuery({
    queryKey: ['school_classes', user?.schoolId],
    queryFn: async () => {
      if (!user?.schoolId) return [];
      
      const { data, error } = await supabase
        .from('school_classes')
        .select('id, name')
        .eq('school_id', user.schoolId)
        .order('sequence_order', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  // Sort classes alphabetically for display in the dropdown
  const sortedClasses = [...classes].sort((a, b) => a.name.localeCompare(b.name));

  const selectedClass = classes.find(cls => cls.name === formData.class);

  const { data: streams = [] } = useQuery({
    queryKey: ['class_streams', user?.schoolId, selectedClass?.id],
    queryFn: async () => {
      if (!user?.schoolId || !selectedClass?.id) return [];
      
      const { data, error } = await supabase
        .from('class_streams')
        .select('name')
        .eq('school_id', user.schoolId)
        .eq('class_id', selectedClass.id)
        .order('name');
      
      if (error) throw error;
      return data;
    },
    enabled: !!selectedClass?.id,
  });

  const handleMedicalAlertChange = (index: number, field: keyof MedicalAlert, value: string) => {
    const newAlerts = [...formData.medical_alerts];
    newAlerts[index] = {
      ...newAlerts[index],
      [field]: value
    };
    setFormData({ ...formData, medical_alerts: newAlerts });
  };

  const addMedicalAlert = () => {
    setFormData({
      ...formData,
      medical_alerts: [...formData.medical_alerts, { type: '', description: '' }]
    });
  };

  const removeMedicalAlert = (index: number) => {
    const newAlerts = formData.medical_alerts.filter((_, i) => i !== index);
    setFormData({ ...formData, medical_alerts: newAlerts });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user?.schoolId) {
      toast({
        title: "Error",
        description: "School ID not found",
        variant: "destructive",
      });
      return;
    }

    if (!formData.class) {
      toast({
        title: "Error",
        description: "Please select a class",
        variant: "destructive",
      });
      return;
    }

    if (!formData.section) {
      toast({
        title: "Error",
        description: "Please select a section (Boarding/Day)",
        variant: "destructive",
      });
      return;
    }

    if (isTISP && formData.section === "BOARDING") {
      if (!formData.dormitory || !formData.dormitory_mother || !formData.dormitory_mother_contact) {
        toast({
          title: "Error",
          description: "Please fill in all dormitory information for TISP boarding students",
          variant: "destructive",
        });
        return;
      }
    }

    try {
      const studentData = {
        ...formData,
        stream: formData.stream || null,
        school_id: user.schoolId,
        sex: formData.sex || null,
        section: formData.section || "DAY",
        dormitory: formData.section === "BOARDING" ? formData.dormitory : null,
        dormitory_mother: formData.section === "BOARDING" ? formData.dormitory_mother : null,
        dormitory_mother_contact: formData.section === "BOARDING" ? formData.dormitory_mother_contact : null,
        age: formData.age ? parseInt(formData.age) : null,
      };

      console.log('Saving student data:', studentData);

      if (student) {
        const { error } = await supabase
          .from('students')
          .update(studentData)
          .eq('id', student.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Student updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('students')
          .insert([studentData]);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Student added successfully",
        });
      }

      onClose();
    } catch (error) {
      console.error('Error saving student:', error);
      toast({
        title: "Error",
        description: "Failed to save student",
        variant: "destructive",
      });
    }
  };

  if (schoolLoading) {
    return <div>Loading school data...</div>;
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle>
            {student ? "Edit Student" : "Add New Student"}
          </DialogTitle>
          <DialogDescription>
            Fill in the student details below. Fields marked with * are required.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(90vh-8rem)]">
          <form onSubmit={handleSubmit} className="space-y-4 p-6">
            <div className="space-y-2">
              <Label htmlFor="surname">Surname *</Label>
              <Input
                id="surname"
                value={formData.surname}
                onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="other_names">Other Names</Label>
              <Input
                id="other_names"
                value={formData.other_names}
                onChange={(e) => setFormData({ ...formData, other_names: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="age">Age</Label>
                <Input
                  id="age"
                  type="number"
                  min="0"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sex">Sex</Label>
                <Select
                  value={formData.sex}
                  onValueChange={(value) => setFormData({ ...formData, sex: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select sex..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MALE">Male</SelectItem>
                    <SelectItem value="FEMALE">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="class">Class *</Label>
                <Select
                  value={formData.class}
                  onValueChange={(value) => setFormData({ ...formData, class: value, stream: "" })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select class..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    <ScrollArea className="h-[200px]">
                      {sortedClasses.map((cls) => (
                        <SelectItem key={cls.name} value={cls.name}>
                          {cls.name}
                        </SelectItem>
                      ))}
                    </ScrollArea>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="stream">Stream</Label>
                <Select
                  value={formData.stream}
                  onValueChange={(value) => setFormData({ ...formData, stream: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select stream..." />
                  </SelectTrigger>
                  <SelectContent>
                    {streams.map((stream) => (
                      <SelectItem key={stream.name} value={stream.name}>
                        {stream.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Medical Alerts</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addMedicalAlert}
                >
                  Add Alert
                </Button>
              </div>
              <div className="space-y-4">
                {formData.medical_alerts.map((alert, index) => (
                  <div key={index} className="grid gap-2 border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <Label>Alert {index + 1}</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => removeMedicalAlert(index)}
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Select
                        value={alert.type}
                        onValueChange={(value) => handleMedicalAlertChange(index, 'alert_type' as keyof MedicalAlert, value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ALLERGY">Allergy</SelectItem>
                          <SelectItem value="CHRONIC_CONDITION">Chronic Condition</SelectItem>
                          <SelectItem value="DIETARY_RESTRICTION">Dietary Restriction</SelectItem>
                          <SelectItem value="MEDICATION">Medication</SelectItem>
                          <SelectItem value="OTHER">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="Description"
                        value={alert.description}
                        onChange={(e) => handleMedicalAlertChange(index, 'description', e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="section">Section *</Label>
              <Select
                value={formData.section}
                onValueChange={(value) => setFormData({ ...formData, section: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select section..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DAY">Day</SelectItem>
                  <SelectItem value="BOARDING">Boarding</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.section === "BOARDING" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="dormitory">
                    Dormitory {isTISP && <span className="text-red-500">*</span>}
                  </Label>
                  <Input
                    id="dormitory"
                    value={formData.dormitory}
                    onChange={(e) => setFormData({ ...formData, dormitory: e.target.value })}
                    required={isTISP}
                  />
                </div>

                <div className="space-y-2">
                  <Label>
                    Dormitory Mother {isTISP && <span className="text-red-500">*</span>}
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Name"
                      value={formData.dormitory_mother}
                      onChange={(e) => setFormData({ ...formData, dormitory_mother: e.target.value })}
                      required={isTISP}
                    />
                    <Input
                      placeholder="Contact"
                      value={formData.dormitory_mother_contact}
                      onChange={(e) => setFormData({ ...formData, dormitory_mother_contact: e.target.value })}
                      required={isTISP}
                    />
                  </div>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>Emergency Contact</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Name"
                  value={formData.emergency_contact_name}
                  onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                />
                <Input
                  placeholder="Phone"
                  value={formData.emergency_contact_phone}
                  onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tutor 1</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Name"
                  value={formData.tutor1_name}
                  onChange={(e) => setFormData({ ...formData, tutor1_name: e.target.value })}
                />
                <Input
                  placeholder="Contact"
                  value={formData.tutor1_contact}
                  onChange={(e) => setFormData({ ...formData, tutor1_contact: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tutor 2</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Name"
                  value={formData.tutor2_name}
                  onChange={(e) => setFormData({ ...formData, tutor2_name: e.target.value })}
                />
                <Input
                  placeholder="Contact"
                  value={formData.tutor2_contact}
                  onChange={(e) => setFormData({ ...formData, tutor2_contact: e.target.value })}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">
                {student ? "Update" : "Add"} Student
              </Button>
            </div>
          </form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
